import { useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOptionalOrganization } from "@/hooks/useOrganization";
import type { Json } from "@/integrations/supabase/types";
import {
  getEventConfig,
  isCriticalEvent,
  isAnalyticsTelemetryEnabled,
  isFrequentTelemetryEnabled,
} from "@/lib/telemetry-config";

/**
 * Action Telemetry Hook
 *
 * Tracks user interactions across the application for analytics and debugging.
 * Events are logged to the platform_events table via RPC.
 *
 * Event Tiers:
 * - CRITICAL: Always tracked (errors, security events, admin actions)
 * - ANALYTICS: Toggle-able, respects user/org preferences and sampling
 *
 * Categories:
 * - 'system': UI interactions, page views, navigation, filters
 * - 'user': User-initiated actions (CRUD operations)
 * - 'organization': Org-level actions
 *
 * This is a fire-and-forget system - failures are silently logged.
 */

type ActionCategory = "system" | "user" | "organization";
type TelemetryStatus = "success" | "failure";

interface TrackActionParams {
  action: string;
  category?: ActionCategory;
  metadata?: Record<string, unknown>;
  source?: string;
  status?: TelemetryStatus;
}

interface TrackNavigationParams {
  page: string;
  metadata?: Record<string, unknown>;
  status?: TelemetryStatus;
}

interface TrackFilterParams {
  filterType: string;
  filterValue: string | string[] | null;
  source: string;
  metadata?: Record<string, unknown>;
  status?: TelemetryStatus;
}

interface TrackSearchParams {
  query: string;
  resultCount?: number;
  source: string;
  metadata?: Record<string, unknown>;
  status?: TelemetryStatus;
}

interface TrackCRUDParams {
  operation: "create" | "read" | "update" | "delete" | "status_change";
  resourceType: string;
  source?: string;
  resourceId?: string;
  metadata?: Record<string, unknown>;
  status?: TelemetryStatus;
}

interface TrackExportParams {
  format: string;
  source: string;
  itemCount?: number;
  metadata?: Record<string, unknown>;
  status?: TelemetryStatus;
}

interface TrackDialogParams {
  dialogName: string;
  action: "opened" | "closed" | "submitted" | "cancelled";
  metadata?: Record<string, unknown>;
  status?: TelemetryStatus;
}

interface TrackTabParams {
  tabName: string;
  source: string;
  previousTab?: string;
  metadata?: Record<string, unknown>;
  status?: TelemetryStatus;
}

// Generate a stable session ID for correlating events
function getSessionId(): string {
  if (typeof window === "undefined") return "";
  try {
    let sessionId = window.sessionStorage.getItem("action_session_id");
    if (!sessionId) {
      sessionId = crypto.randomUUID?.() ?? `action-${Math.random().toString(36).slice(2, 10)}`;
      window.sessionStorage.setItem("action_session_id", sessionId);
    }
    return sessionId;
  } catch {
    return "";
  }
}

export function useActionTelemetry() {
  const { user } = useAuth();
  const organizationContext = useOptionalOrganization();
  const organization = organizationContext?.organization ?? null;
  const sessionId = useMemo(() => getSessionId(), []);

  /**
   * Check if an event should be logged based on its tier and sampling rate
   */
  const shouldLogEvent = useCallback((eventType: string): boolean => {
    const config = getEventConfig(eventType);

    // Critical events are always logged
    if (isCriticalEvent(eventType)) {
      return true;
    }

    // Analytics events respect the toggle setting
    if (!isAnalyticsTelemetryEnabled()) {
      return false;
    }

    // If frequent telemetry is enabled, bypass sampling and log everything
    if (isFrequentTelemetryEnabled()) {
      return true;
    }

    // Apply sampling rate if configured
    if (config.sampleRate !== undefined && config.sampleRate < 1) {
      return Math.random() < config.sampleRate;
    }

    return true;
  }, []);

  const logEvent = useCallback(
    async (
      category: ActionCategory,
      eventType: string,
      metadata?: Record<string, unknown>,
      source?: string,
      status: TelemetryStatus = "success"
    ) => {
      // Check if this event should be logged
      if (!shouldLogEvent(eventType)) {
        return;
      }

      try {
        const config = getEventConfig(eventType);
        const fullMetadata: Record<string, unknown> = {
          ...(metadata ?? {}),
          sessionId,
          timestamp: new Date().toISOString(),
          path: typeof window !== "undefined" ? window.location.pathname : null,
          eventTier: config.tier,
        };

        await supabase.rpc("log_platform_event", {
          p_category: category,
          p_event_type: eventType,
          p_status: status,
          p_user_id: user?.id ?? null,
          p_organization_id: organization?.id ?? null,
          p_message: null,
          p_metadata: fullMetadata as Json,
          p_source: source ?? "client",
          p_ip_address: null,
          p_user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        });
      } catch (error) {
        // Silent fail - telemetry should never block user actions
        console.debug("[action-telemetry] Error logging event:", error);
      }
    },
    [user?.id, organization?.id, sessionId, shouldLogEvent]
  );

  /**
   * Track a generic user action
   * Supports both legacy signature (action, metadata) and new signature ({ action, category, metadata, source })
   */
  const trackAction = useCallback(
    (
      actionOrParams: string | TrackActionParams,
      legacyMetadata?: Record<string, unknown>,
      legacyStatus?: TelemetryStatus
    ) => {
      // Handle legacy signature: trackAction("filter_applied", { filter: "..." })
      if (typeof actionOrParams === "string") {
        logEvent("system", actionOrParams, legacyMetadata, undefined, legacyStatus);
        return;
      }
      // Handle new signature: trackAction({ action, category, metadata, source })
      const { action, category = "system", metadata, source, status } = actionOrParams;
      logEvent(category, action, metadata, source, status);
    },
    [logEvent]
  );

  /**
   * Track page navigation / page views
   * Supports both legacy signature (actionType, metadata) and new signature ({ page, metadata })
   */
  const trackNavigation = useCallback(
    (
      actionOrParams: string | TrackNavigationParams,
      legacyMetadata?: Record<string, unknown>,
      legacyStatus?: TelemetryStatus
    ) => {
      // Handle legacy signature: trackNavigation("page_view", { page: "settings" })
      if (typeof actionOrParams === "string") {
        const page = legacyMetadata?.page as string | undefined;
        logEvent("system", actionOrParams, { page, ...legacyMetadata }, "navigation", legacyStatus);
        return;
      }
      // Handle new signature: trackNavigation({ page: "settings", metadata: {} })
      const { page, metadata, status } = actionOrParams;
      logEvent("system", "page_view", { page, ...metadata }, "navigation", status);
    },
    [logEvent]
  );

  /**
   * Track filter/sort interactions
   */
  const trackFilter = useCallback(
    ({ filterType, filterValue, source, metadata, status }: TrackFilterParams) => {
      logEvent(
        "system",
        "filter_applied",
        { filterType, filterValue, ...metadata },
        source,
        status
      );
    },
    [logEvent]
  );

  /**
   * Track search queries (for non-search-palette searches)
   */
  const trackSearch = useCallback(
    ({ query, resultCount, source, metadata, status }: TrackSearchParams) => {
      logEvent(
        "system",
        resultCount === 0 ? "search_zero_results" : "search_query",
        { query, resultCount, ...metadata },
        source,
        status
      );
    },
    [logEvent]
  );

  /**
   * Track CRUD operations
   */
  const trackCRUD = useCallback(
    ({ operation, resourceType, source, resourceId, metadata, status }: TrackCRUDParams) => {
      logEvent(
        "user",
        `${resourceType}_${operation}`,
        { resourceId, ...metadata },
        source ?? resourceType,
        status
      );
    },
    [logEvent]
  );

  /**
   * Track export actions
   */
  const trackExport = useCallback(
    ({ format, source, itemCount, metadata, status }: TrackExportParams) => {
      logEvent(
        "system",
        "export_requested",
        { format, itemCount, ...metadata },
        source,
        status
      );
    },
    [logEvent]
  );

  /**
   * Track dialog open/close/submit events
   * Supports both legacy signature (action, dialogName, metadata?) and new signature ({ dialogName, action, metadata })
   */
  const trackDialog = useCallback(
    (
      actionOrParams: string | TrackDialogParams,
      dialogNameOrMetadata?: string | Record<string, unknown>,
      legacyMetadata?: Record<string, unknown>,
      legacyStatus?: TelemetryStatus
    ) => {
      // Handle legacy signature: trackDialog("dialog_opened", "create_event", { ... })
      if (typeof actionOrParams === "string") {
        const action = actionOrParams as TrackDialogParams["action"];
        const dialogName = typeof dialogNameOrMetadata === "string" ? dialogNameOrMetadata : undefined;
        const metadata = typeof dialogNameOrMetadata === "object" ? dialogNameOrMetadata : legacyMetadata;
        logEvent(
          "system",
          `dialog_${action.replace("dialog_", "")}`,
          { dialogName, ...metadata },
          "dialog",
          legacyStatus
        );
        return;
      }
      // Handle new signature: trackDialog({ dialogName, action, metadata })
      const { dialogName, action, metadata, status } = actionOrParams;
      logEvent("system", `dialog_${action}`, { dialogName, ...metadata }, "dialog", status);
    },
    [logEvent]
  );

  /**
   * Track tab switches
   * Supports both legacy signature (action, metadata) and new signature ({ tabName, source, previousTab, metadata })
   */
  const trackTab = useCallback(
    (
      actionOrParams: string | TrackTabParams,
      legacyMetadata?: Record<string, unknown>,
      legacyStatus?: TelemetryStatus
    ) => {
      // Handle legacy signature: trackTab("tab_switch", { tab, page })
      if (typeof actionOrParams === "string") {
        const tabName = legacyMetadata?.tab as string | undefined;
        logEvent(
          "system",
          "tab_switch",
          { tabName, ...legacyMetadata },
          legacyMetadata?.page as string | undefined,
          legacyStatus
        );
        return;
      }
      // Handle new signature: trackTab({ tabName, source, previousTab, metadata })
      const { tabName, source, previousTab, metadata, status } = actionOrParams;
      logEvent(
        "system",
        "tab_switch",
        { tabName, previousTab, ...metadata },
        source,
        status
      );
    },
    [logEvent]
  );

  /**
   * Track button clicks for important actions
   */
  const trackButtonClick = useCallback(
    (
      buttonName: string,
      source: string,
      metadata?: Record<string, unknown>,
      status?: TelemetryStatus
    ) => {
      logEvent("system", "button_click", { buttonName, ...metadata }, source, status);
    },
    [logEvent]
  );

  /**
   * Track critical events (errors, security events)
   * These bypass the analytics toggle and are always logged
   */
  const trackCriticalEvent = useCallback(
    async (
      eventType: string,
      metadata?: Record<string, unknown>,
      source?: string
    ) => {
      try {
        const fullMetadata: Record<string, unknown> = {
          ...(metadata ?? {}),
          sessionId,
          timestamp: new Date().toISOString(),
          path: typeof window !== "undefined" ? window.location.pathname : null,
          eventTier: "critical",
        };

        // Always log critical events regardless of analytics toggle
        await supabase.rpc("log_platform_event", {
          p_category: "system",
          p_event_type: eventType,
          p_status: metadata?.status === "failure" ? "failure" : "success",
          p_user_id: user?.id ?? null,
          p_organization_id: organization?.id ?? null,
          p_message: (metadata?.message as string) ?? null,
          p_metadata: fullMetadata as Json,
          p_source: source ?? "client",
          p_ip_address: null,
          p_user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        });
      } catch (error) {
        console.error("[action-telemetry] Error logging critical event:", error);
      }
    },
    [user?.id, organization?.id, sessionId]
  );

  /**
   * Track client-side errors (always logged, triggers push notification)
   */
  const trackError = useCallback(
    (error: Error | string, context?: Record<string, unknown>) => {
      const errorMessage = typeof error === "string" ? error : error.message;
      const errorStack = typeof error === "string" ? undefined : error.stack;

      trackCriticalEvent("client_error", {
        status: "failure",
        message: errorMessage,
        errorStack,
        ...context,
      }, "error-boundary");
    },
    [trackCriticalEvent]
  );

  return {
    trackAction,
    trackNavigation,
    trackFilter,
    trackSearch,
    trackCRUD,
    trackExport,
    trackDialog,
    trackTab,
    trackButtonClick,
    trackCriticalEvent,
    trackError,
  };
}
